import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import APIGateway from '@/api';
import {
  MetricConfig,
  MetricCreateReq, MetricProvider, MetricsGetRes,
  MetricsSearchParams, MetricsSearchRes
} from '@/types/evals';


export const metricsKeys = {
  all: [ 'metrics' ] as const,
  list: () => [ ...metricsKeys.all, 'list' ] as const,
  search: ( params?: MetricsSearchParams ) => [ ...metricsKeys.all, 'search', params ] as const,
  config: ( metricId: string, version: number ) => [ ...metricsKeys.all, 'config', metricId, version ] as const
};


export function useMetricsQuery() {
  return useQuery( {
    queryKey: metricsKeys.list(),
    queryFn: async() => {
      const metrics = await APIGateway.evals.metricsGet();

      return metrics;
    }
  } );
}

export function useMetricConfigQuery(
  metricId: string | undefined,
  version: number | undefined
) {
  return useQuery( {
    queryKey: metricsKeys.config( metricId ?? '', version ?? 0 ),
    queryFn: async() => {
      if ( !metricId || version === undefined ) throw new Error( 'Metric ID and version required' );
      const config = await APIGateway.evals.metricConfigGet( metricId, version );
      return config;
    },
    enabled: !!metricId && version !== undefined,
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  } );
}

export function useMetricsSearch( params: MetricsSearchParams ) {
  return useQuery<{ items: MetricConfig[]; total: number }>( {
    refetchOnWindowFocus: false,
    queryKey: metricsKeys.search( params ),
    queryFn: async() => {
      const res: MetricsSearchRes = await APIGateway.evals.metricsSearch( params );
      return { items: res.items, total: res.pagination.total };
    },
    placeholderData: keepPreviousData
  } );
}

export function useCreateMetricMutation() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: MetricCreateReq ) => {
      const result = await APIGateway.evals.metricCreate( args );

      return result;
    },
    onSuccess: () => {
      // Invalidate and refetch metrics list
      queryClient.invalidateQueries( { queryKey: metricsKeys.all } );
    }
  } );
}
